import { toDefectRulesetSlug } from '../contracts/defectRulesets';
import {
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import {
  Recipe,
  RecipeComponent,
  RecipeLegendItem,
} from '../contracts/recipes';
import {
  AvailableViewsResponseBody,
  toAvailableViewSummary,
} from '../contracts/availableViews';
import {
  CRACKING_RAG_LEGEND_ITEMS,
  CRACKING_RAG_THRESHOLD_ATTRIBUTE,
} from '../crackmaps/crackingBands';
import {
  LINE_CONDITION_RAG_LEGEND_ITEMS,
  LINE_CONDITION_RAG_THRESHOLD_ATTRIBUTE,
} from '../lineConditions/lineConditionBands';
import {
  PAS2161_VALUE_LEGEND_ITEMS,
  PAS2161_VALUE_THRESHOLD_ATTRIBUTE,
} from '../pas2161/pas2161Bands';
import {
  formatPas2161RegionClientLabel,
  formatPas2161RegionLabel,
  PAS2161_OVERLAY_TENANT_SLUG,
  PAS2161_PASSES,
  PAS2161_REGIONS,
  PAS2161_SURVEY_YEAR,
  pas2161RunFor,
  type Pas2161Pass,
  type Pas2161Region,
} from '../pas2161/pas2161LayerModel';
import {
  POTHOLE_RAG_LEGEND_ITEMS,
  POTHOLE_RAG_THRESHOLD_ATTRIBUTE,
} from '../potholes/potholeBands';

function legend(
  items: readonly RecipeLegendItem[],
  thresholdAttribute: string | null = null,
): {
  readonly legendThresholdAttribute: string | null;
  readonly legendItems: readonly RecipeLegendItem[];
  readonly legendColors: readonly string[];
} {
  return {
    legendThresholdAttribute: thresholdAttribute,
    legendItems: items,
    legendColors: items.map((item) => item.color),
  };
}

function pas2161ValueLegend(): {
  readonly legendThresholdAttribute: string | null;
  readonly legendItems: readonly RecipeLegendItem[];
  readonly legendColors: readonly string[];
} {
  return legend(PAS2161_VALUE_LEGEND_ITEMS, PAS2161_VALUE_THRESHOLD_ATTRIBUTE);
}

function component(
  partial: Omit<
    RecipeComponent,
    | 'rulesetSlug'
    | 'yearRule'
    | 'fixedSurveyYearSlug'
    | 'fixedLanes'
    | 'visibleByDefault'
    | 'defectSlug'
    | 'measurementSlug'
    | 'featureFilter'
  > &
    Partial<
      Pick<
        RecipeComponent,
        | 'rulesetSlug'
        | 'yearRule'
        | 'fixedSurveyYearSlug'
        | 'fixedLanes'
        | 'visibleByDefault'
        | 'defectSlug'
        | 'measurementSlug'
        | 'featureFilter'
      >
    >,
): RecipeComponent {
  return {
    defectSlug: partial.defectSlug ?? null,
    measurementSlug: partial.measurementSlug ?? null,
    rulesetSlug: partial.rulesetSlug ?? toDefectRulesetSlug('default'),
    yearRule: partial.yearRule ?? 'LATEST',
    fixedSurveyYearSlug: partial.fixedSurveyYearSlug ?? null,
    fixedLanes: partial.fixedLanes ?? null,
    visibleByDefault: partial.visibleByDefault ?? true,
    featureFilter: partial.featureFilter ?? null,
    id: partial.id,
    label: partial.label,
    surveyTypeHint: partial.surveyTypeHint,
    measurementNameHint: partial.measurementNameHint,
    laneRule: partial.laneRule,
    displayOrder: partial.displayOrder,
  };
}

function pas2161FixedSurveyYear(
  region: Pas2161Region,
  pass: Pas2161Pass,
): Pick<RecipeComponent, 'yearRule' | 'fixedSurveyYearSlug'> {
  const run = pas2161RunFor(region, pass);
  return {
    yearRule: 'FIXED',
    fixedSurveyYearSlug: toDefectSurveyYearSlug(
      `${PAS2161_OVERLAY_TENANT_SLUG}-${run}-${PAS2161_SURVEY_YEAR}`,
    ),
  };
}

function buildPas2161PassRecipes(
  region: Pas2161Region,
  pass: Pas2161Pass,
): readonly Recipe[] {
  const label = formatPas2161RegionClientLabel(region);
  const regionName = formatPas2161RegionLabel(region);
  const fixedYear = pas2161FixedSurveyYear(region, pass);
  const nameSuffix = ` · ${pass}`;

  return [
    {
      id: `pts-pas2161-${region}-mini-potholes-${pass}`,
      name: `${label} Mini MFV – Potholes${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV potholes (${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-potholes-${pass}-overlay`,
          label: 'Potholes',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-d40`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-d40-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Potholes',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mini-longitudinal-${pass}`,
      name: `${label} Mini MFV – Longitudinal cracks${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV longitudinal cracks (d00, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-longitudinal-${pass}-overlay`,
          label: 'Longitudinal cracks',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-d00`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-d00-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Longitudinal cracks',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mini-transversal-${pass}`,
      name: `${label} Mini MFV – Transversal cracks${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV transversal cracks (d10, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-transversal-${pass}-overlay`,
          label: 'Transversal cracks',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-d10`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-d10-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Transversal cracks',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mini-alligator-${pass}`,
      name: `${label} Mini MFV – Alligator cracks${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV alligator cracks (d20, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-alligator-${pass}-overlay`,
          label: 'Alligator cracks',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-d20`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-d20-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Alligator cracks',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mini-patches-${pass}`,
      name: `${label} Mini MFV – Patches${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV patches (p00, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-patches-${pass}-overlay`,
          label: 'Patches',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-p00`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-p00-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Patches',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mini-iron-work-${pass}`,
      name: `${label} Mini MFV – Iron work${nameSuffix}`,
      description: '',
      purpose: `${label} Mini MFV iron work (i00, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mini-iron-work-${pass}-overlay`,
          label: 'Iron work',
          defectSlug: toDefectSlug(`pas-2161-${region}-m-mfv-i00`),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-i00-overlay`,
          ),
          surveyTypeHint: 'm-mfv',
          measurementNameHint: 'Iron work',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mfv-cracking-relaxed-${pass}`,
      name: `${label} MFV – Cracking (Relaxed)${nameSuffix}`,
      description: '',
      purpose: `${label} MFV relaxed cracking (${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mfv-cracking-relaxed-${pass}-overlay`,
          label: 'Cracking (Relaxed)',
          defectSlug: toDefectSlug(
            `pas-2161-${region}-mfv-relaxed-cracking`,
          ),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-mfv-relaxed-overlay`,
          ),
          surveyTypeHint: 'mfv',
          measurementNameHint: 'Cracking (Relaxed)',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
    {
      id: `pts-pas2161-${region}-mfv-cracking-accred-${pass}`,
      name: `${label} MFV – Cracking (Accredited)${nameSuffix}`,
      description: '',
      purpose: `${label} MFV accredited cracking (${regionName}, ${pass})`,
      ownerType: 'PTS',
      groupId: 'ptsStandard',
      visibleByDefault: false,
      ...pas2161ValueLegend(),
      editable: false,
      components: [
        component({
          id: `pts-pas2161-${region}-mfv-cracking-accred-${pass}-overlay`,
          label: 'Cracking (Accredited)',
          defectSlug: toDefectSlug(
            `pas-2161-${region}-mfv-accred-cracking`,
          ),
          measurementSlug: toDefectMeasurementSlug(
            `pas2161-${region}-mfv-accred-overlay`,
          ),
          surveyTypeHint: 'mfv',
          measurementNameHint: 'Cracking (Accredited)',
          laneRule: 'FIRST',
          displayOrder: 1,
          ...fixedYear,
        }),
      ],
    },
  ];
}

const PAS2161_MOCK_RECIPES: readonly Recipe[] = PAS2161_REGIONS.flatMap(
  (region) =>
    PAS2161_PASSES.flatMap((pass) => buildPas2161PassRecipes(region, pass)),
);

const WINDSOR_MOCK_RECIPES: readonly Recipe[] = [
  {
    id: 'pts-mfv-cracking',
    name: 'MFV Cracking',
    description: '',
    purpose:
      'Inspect individual crack vectors on the network with width-based RAG banding',
    ownerType: 'PTS',
    groupId: 'ptsStandard',
    visibleByDefault: false,
    ...legend(CRACKING_RAG_LEGEND_ITEMS, CRACKING_RAG_THRESHOLD_ATTRIBUTE),
    editable: false,
    components: [
      component({
        id: 'pts-mfv-cracking-overlay',
        label: 'Cracking',
        defectSlug: toDefectSlug('windsor-mfv-crackmaps'),
        measurementSlug: toDefectMeasurementSlug('crackmap-overlay'),
        surveyTypeHint: 'mfv',
        measurementNameHint: 'Cracking',
        laneRule: 'FIRST',
        displayOrder: 1,
      }),
    ],
  },
  {
    id: 'pts-mfv-potholes',
    name: 'MFV Potholes',
    description: '',
    purpose:
      'Inspect individual pothole polygons on the network with depth-based RAG banding',
    ownerType: 'PTS',
    groupId: 'ptsStandard',
    visibleByDefault: false,
    ...legend(POTHOLE_RAG_LEGEND_ITEMS, POTHOLE_RAG_THRESHOLD_ATTRIBUTE),
    editable: false,
    components: [
      component({
        id: 'pts-mfv-potholes-overlay',
        label: 'Potholes',
        defectSlug: toDefectSlug('windsor-mfv-potholes'),
        measurementSlug: toDefectMeasurementSlug('pothole-overlay'),
        surveyTypeHint: 'mfv',
        measurementNameHint: 'Potholes',
        laneRule: 'FIRST',
        displayOrder: 1,
      }),
    ],
  },
  {
    id: 'pts-mfv-line-condition',
    name: 'MFV Line Condition',
    description: '',
    purpose:
      'Inspect line-condition geometry on the network with condition-based RAG banding',
    ownerType: 'PTS',
    groupId: 'ptsStandard',
    visibleByDefault: false,
    ...legend(LINE_CONDITION_RAG_LEGEND_ITEMS, LINE_CONDITION_RAG_THRESHOLD_ATTRIBUTE),
    editable: false,
    components: [
      component({
        id: 'pts-mfv-line-condition-overlay',
        label: 'Line Condition',
        defectSlug: toDefectSlug('windsor-mfv-line-condition'),
        measurementSlug: toDefectMeasurementSlug('line-condition-overlay'),
        surveyTypeHint: 'mfv',
        measurementNameHint: 'Line Condition',
        laneRule: 'FIRST',
        displayOrder: 1,
      }),
    ],
  },
];

export const MOCK_RECIPES: readonly Recipe[] = [
  ...WINDSOR_MOCK_RECIPES,
  ...PAS2161_MOCK_RECIPES,
];

export function buildMockAvailableViewsResponse(): AvailableViewsResponseBody {
  return {
    views: MOCK_RECIPES.map(toAvailableViewSummary),
  };
}
